import { generateKeyPair, keyPairFromSecret, type KeyPair } from "./keys";

/**
 * Where a device keeps its long-lived secrets. The interface is platform-free so each
 * target supplies its own backing: OS keychain or a 0600 file on the desktop daemon,
 * IndexedDB (non-extractable key) in the PWA, platform secure storage in a native app.
 * Only the 32-byte private key is ever stored; it never leaves the device.
 */
export interface SecretStore {
  /** Human-readable backing ("macOS Keychain", "file"), for diagnostics. */
  readonly description: string;
  get(name: string): Promise<Uint8Array | null>;
  set(name: string, value: Uint8Array): Promise<void>;
  delete(name: string): Promise<boolean>;
}

/** Load this device's identity from the store, creating and saving one on first use. */
export async function loadOrCreateIdentity(store: SecretStore, name: string): Promise<KeyPair> {
  const existing = await store.get(name);
  if (existing) return keyPairFromSecret(existing);
  const keys = generateKeyPair();
  await store.set(name, keys.secretKey);
  return keys;
}

/** In-memory store, for tests. */
export class MemorySecretStore implements SecretStore {
  readonly description = "memory";
  private readonly values = new Map<string, Uint8Array>();
  async get(name: string) {
    const v = this.values.get(name);
    return v ? v.slice() : null;
  }
  async set(name: string, value: Uint8Array) {
    this.values.set(name, value.slice());
  }
  async delete(name: string) {
    return this.values.delete(name);
  }
}
